import { BusMessage, MessageFactory, UID_BYTES_PER_ELEMENT, FacetFactory, BOOLEAN_BYTES_PER_ELEMENT, UINT32_BYTES_PER_ELEMENT, NUMBER16_BYTES_PER_ELEMENT, } from "flavr-common";
/**
 * Announces that a room transform changed.
 */
export class RoomTransformationUpdateMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + Float32Array.BYTES_PER_ELEMENT * 16;
    }
    constructor(uid, transform) {
        super();
        this.uid = uid;
        this.transform = transform;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.transform = stream.number16(this.transform);
    }
    static is(msg) {
        return msg.type === RoomTransformationUpdateMessage.typestatic;
    }
}
RoomTransformationUpdateMessage.typestatic = "RoomTransformationUpdateMessage";
RoomTransformationUpdateMessage.subjectstatic = "scenegraph";
MessageFactory.register(RoomTransformationUpdateMessage);
/**
 * Announces an auto-scale children transform update.
 */
export class AutoScaleChildrenTransformUpdateMessage extends BusMessage {
    get byteLength() {
        return AutoScaleChildrenTransformUpdateMessage.MAX_BYTE_LENGTH;
    }
    constructor(uid, offset, scale) {
        super();
        this.uid = uid;
        this.offset = offset;
        this.scale = scale;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        this.offset = stream.number3(this.offset);
        this.scale = stream.float32(this.scale);
    }
    static is(msg) {
        return msg.type === AutoScaleChildrenTransformUpdateMessage.typestatic;
    }
}
AutoScaleChildrenTransformUpdateMessage.typestatic = "AutoScaleChildrenTransformUpdateMessage";
AutoScaleChildrenTransformUpdateMessage.subjectstatic = "scenegraph";
AutoScaleChildrenTransformUpdateMessage.MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
    Float32Array.BYTES_PER_ELEMENT * 3 + // offset
    Float32Array.BYTES_PER_ELEMENT; // scale
MessageFactory.register(AutoScaleChildrenTransformUpdateMessage);
/**
 * Updates a model's local transformation.
 */
export class UpdateLocalTransformationMessage extends BusMessage {
    get byteLength() {
        return UpdateLocalTransformationMessage.MAX_BYTE_LENGTH;
    }
    constructor(uid, transform, resultOfTimeStamp) {
        super();
        this.uid = uid;
        this.transform = transform;
        this.resultOfTimeStamp = resultOfTimeStamp;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        if (stream.isReading)
            this.transform = [];
        for (let i = 0; i < 16; ++i) {
            this.transform[i] = stream.float32(this.transform[i]);
        }
        this.resultOfTimeStamp = stream.uint32Safe(this.resultOfTimeStamp);
    }
    static is(msg) {
        return msg.type === UpdateLocalTransformationMessage.typestatic;
    }
}
UpdateLocalTransformationMessage.typestatic = "UpdateLocalTransformationMessage";
UpdateLocalTransformationMessage.subjectstatic = "scenegraph";
UpdateLocalTransformationMessage.MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
    NUMBER16_BYTES_PER_ELEMENT + // transform
    UINT32_BYTES_PER_ELEMENT; // resultOfTimeStamp
MessageFactory.register(UpdateLocalTransformationMessage);
/**
 * Updates a model's world transformation.
 */
export class UpdateWorldTransformationMessage extends BusMessage {
    get byteLength() {
        return UpdateWorldTransformationMessage.MAX_BYTE_LENGTH;
    }
    constructor(uid, transform, boundingBoxMin, boundingBoxMax, resultOfTimeStamp) {
        super();
        this.uid = uid;
        this.transform = transform;
        this.boundingBoxMin = boundingBoxMin;
        this.boundingBoxMax = boundingBoxMax;
        this.resultOfTimeStamp = resultOfTimeStamp;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
        if (stream.isReading)
            this.transform = [];
        for (let i = 0; i < 16; ++i) {
            this.transform[i] = stream.float32(this.transform[i]);
        }
        this.boundingBoxMin = stream.number3Safe(this.boundingBoxMin);
        this.boundingBoxMax = stream.number3Safe(this.boundingBoxMax);
        this.resultOfTimeStamp = stream.uint32Safe(this.resultOfTimeStamp);
    }
    static is(msg) {
        return msg.type === UpdateWorldTransformationMessage.typestatic;
    }
}
UpdateWorldTransformationMessage.typestatic = "UpdateWorldTransformationMessage";
UpdateWorldTransformationMessage.subjectstatic = "scenegraph";
UpdateWorldTransformationMessage.MAX_BYTE_LENGTH = UID_BYTES_PER_ELEMENT + // uid
    NUMBER16_BYTES_PER_ELEMENT + // transform
    Float32Array.BYTES_PER_ELEMENT * 6 + // boundingBox
    UINT32_BYTES_PER_ELEMENT; // resultOfTimeStamp
MessageFactory.register(UpdateWorldTransformationMessage);
/**
 * Requests the transform of a room.
 */
export class RequestRoomTransformMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT;
    }
    constructor(uid) {
        super();
        this.uid = uid;
    }
    serialization(stream) {
        this.uid = stream.uid(this.uid);
    }
    static is(msg) {
        return msg.type === RequestRoomTransformMessage.typestatic;
    }
}
RequestRoomTransformMessage.typestatic = "RequestRoomTransformMessage";
RequestRoomTransformMessage.subjectstatic = "scenegraph";
MessageFactory.register(RequestRoomTransformMessage);
/**
 * Returns the transform of a room.
 */
export class RoomTransformMessage extends BusMessage {
    get byteLength() {
        return Float32Array.BYTES_PER_ELEMENT * 16 + BOOLEAN_BYTES_PER_ELEMENT; // layoutCreationFinished
    }
    constructor(worldMatrix, layoutCreationFinished) {
        super();
        this.worldMatrix = worldMatrix;
        this.layoutCreationFinished = layoutCreationFinished;
    }
    serialization(stream) {
        this.worldMatrix = stream.number16(this.worldMatrix);
        this.layoutCreationFinished = stream.boolean(this.layoutCreationFinished);
    }
    static is(msg) {
        return msg.type === RoomTransformMessage.typestatic;
    }
}
RoomTransformMessage.typestatic = "RoomTransformMessage";
RoomTransformMessage.subjectstatic = "scenegraph";
MessageFactory.register(RoomTransformMessage);
/**
 * Carries facet data that should be applied to a model.
 */
export class ModelFacetMessage extends BusMessage {
    get byteLength() {
        return (UID_BYTES_PER_ELEMENT + // uid
            UID_BYTES_PER_ELEMENT + // facetTypeHash
            BOOLEAN_BYTES_PER_ELEMENT + // has facet
            (this.facet !== undefined ? this.facet.byteLength : 0));
    }
    constructor(uid, facet) {
        super();
        this.uid = uid;
        this.facet = facet;
    }
    serialization(stream) {
        var _a;
        this.uid = stream.uid(this.uid);
        const facetTypeHash = stream.typeHash(this.facet !== undefined ? this.facet.typeHash : 0);
        if (stream.boolean(this.facet !== undefined)) {
            if (stream.isReading) {
                this.facet = FacetFactory.create(facetTypeHash);
            }
            (_a = this.facet) === null || _a === void 0 ? void 0 : _a.serialization(stream);
        }
    }
    static is(msg) {
        return msg.type === ModelFacetMessage.typestatic;
    }
}
ModelFacetMessage.typestatic = "ModelFacetMessage";
ModelFacetMessage.subjectstatic = "scenegraph";
MessageFactory.register(ModelFacetMessage);
/**
 * Announces that world transforms are ready for use.
 */
export class WorldTransformsReadyMessage extends BusMessage {
    serialization() { }
    static is(msg) {
        return msg.type === WorldTransformsReadyMessage.typestatic;
    }
}
WorldTransformsReadyMessage.typestatic = "WorldTransformsReadyMessage";
WorldTransformsReadyMessage.subjectstatic = "scenegraph";
MessageFactory.register(WorldTransformsReadyMessage);
/**
 * Requests attaching one model to another in the scenegraph.
 */
export class AttachToMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + Float32Array.BYTES_PER_ELEMENT * 3; // offset
    }
    constructor(uid, offset) {
        super();
        this.uid = uid;
        this.offset = offset;
    }
    serialization() { }
    static is(msg) {
        return msg.type === AttachToMessage.typestatic;
    }
}
AttachToMessage.typestatic = "AttachToMessage";
AttachToMessage.subjectstatic = "scenegraph";
MessageFactory.register(AttachToMessage);
/**
 * This message is sent each time the bounds of this model changed. The bounds are in world coordinates
 */
export class MeshBoundingMessage extends BusMessage {
    get byteLength() {
        return UID_BYTES_PER_ELEMENT + Float32Array.BYTES_PER_ELEMENT * (3 + 3 + 3 + 1);
    }
    constructor(modelUID, boundingBoxMin, boundingBoxMax, boundingSphereCenter, boundingSphereRadius) {
        super();
        this.modelUID = modelUID;
        this.boundingBoxMin = boundingBoxMin;
        this.boundingBoxMax = boundingBoxMax;
        this.boundingSphereCenter = boundingSphereCenter;
        this.boundingSphereRadius = boundingSphereRadius;
    }
    serialization(stream) {
        this.modelUID = stream.uid(this.modelUID);
        this.boundingBoxMin = stream.number3(this.boundingBoxMin);
        this.boundingBoxMax = stream.number3(this.boundingBoxMax);
        this.boundingSphereCenter = stream.number3(this.boundingSphereCenter);
        this.boundingSphereRadius = stream.float32(this.boundingSphereRadius);
    }
    static is(msg) {
        return msg.type === MeshBoundingMessage.typestatic;
    }
}
MeshBoundingMessage.typestatic = "MeshBoundingMessage";
MeshBoundingMessage.subjectstatic = "general";
MessageFactory.register(MeshBoundingMessage);
